import React, { useState } from 'react';
import { Card, Panel, Table } from '@/components/Bicester';
import { Cell, DateTimeCell, LevelCell, UserNameCell } from '@/components/Bicester/Table/Cells';
import { useRequest } from 'ahooks';
import { ColumnConfig } from '@/components/Bicester/Table';
import { BICESTER_FULL_DATE, BICESTER_FULL_DATE_HOURS_MINUTES } from '@/constants/dateFormat';
import { IconButton } from 'rsuite';
import Image from '@/components/Bicester/Image';
import { FormikProvider, useFormik } from 'formik';
import { Form } from '@/components/formik-rsuite';
import useContentHeight from '@/hooks/useContentHeight';
import { defaultInfo, transformFormData } from './constants';
import {
  OptionCell,
  SubscribeDateCell,
  SubscribeDetailCell,
  SubscribeOperationCell,
  SubscribeStatusCell,
  TimeSlotCell,
} from './Cell';
import {
  fetchSubscribeList,
  fetchSubscribeSend,
  fetchUpdateOption,
  fetchUpdateStatus,
  fetchUpdateSubscribe,
  fetchUpdateView,
} from '@/services/subscribe/manage';
import useTable from '@/hooks/useTable';
import useSubscribeOption from '@/hooks/preload/useSubscribeOption';
import { useReadyInfo } from '@/providers/ReadyProvider';
import { Bicester } from '@/constants';
import { FORM_DEFAULT_DISPLAY } from '@/constants/config';
import { fetchSubscribeSurplus } from '@/services/subscribe';
import { LAYOUT_CONFIG } from '@/constants/layoutConfig';
import _ from 'lodash';

const Manage = () => {
  const { profile } = useReadyInfo();
  const { data: subscribeOption, loading: subscribeOptionLoading } = useSubscribeOption();

  const [data, setData] = useState([]);

  const {
    run: getSubscribeList,
    data: {
      content_list: subscribeListData,
      current_page,
      total,
      vip_counts_today,
      sa_counts_today,
    } = {},
    loading: subscribeListLoading,
  } = useRequest<any>(fetchSubscribeList, {
    defaultParams: { page: 1, ..._.omit(_.pickBy(transformFormData(defaultInfo)), 'time_range') },
    onSuccess: (res) => getList(res?.content_list),
  });
  const { run: updateSubscribeDate, loading: updateSubscribeDateLoading } = useRequest(
    fetchUpdateSubscribe,
    {
      manual: true,
    }
  );
  const { run: updateOption, loading: updateOptionLoading } = useRequest(fetchUpdateOption, {
    manual: true,
  });
  const { run: subscribeSend, loading: subscribeSendLoading } = useRequest(fetchSubscribeSend, {
    manual: true,
  });
  const { run: updateStatus, loading: updateStatusLoading } = useRequest(fetchUpdateStatus, {
    manual: true,
  });
  const { run: updateView, loading: updateViewLoading } = useRequest(fetchUpdateView, {
    manual: true,
  });
  const { run: getSurplusCount, loading: getSurplusCountLoading } = useRequest(
    fetchSubscribeSurplus,
    {
      manual: true,
    }
  );

  const { onChange, onSearch, onRefresh } = useTable(getSubscribeList, {
    start_at: defaultInfo.time_range[0] || '',
    end_at: defaultInfo.time_range[1] || '',
  });

  const formik = useFormik({
    initialValues: defaultInfo,
    onSubmit(values, actions) {
      onSearch(transformFormData(values));
    },
  });

  const getList = (listData) => {
    const data = (
      listData?.map((data) => {
        const childrenRow = [
          ...(data?.option || []),
          {
            option_name: `备注：${data?.remark || FORM_DEFAULT_DISPLAY}`,
            subscribe_id: data?.id,
            // 是否显示更新按钮 1是 0否 是才显示更新确认按钮
            is_update: data?.is_update,
            // 更新确认按钮 0灰色 更新已确认  1橙色 有更新待确认
            is_update_view: data?.is_update_view,
            // 是否有更新 1有更新 当有更新状态并pass有数据显示pass字段
            ...(data?.is_update === 1 &&
              data?.pass?.option &&
              data?.pass?.option?.length && { pass: data?.pass }),
          },
        ];

        return childrenRow.map((rowData, index) => {
          return {
            ...rowData,
            create_at: data?.create_at,
            member_id: data?.member_id,
            user_name: data?.user_name,
            sex: data?.sex,
            phone: data?.phone,
            level: data?.level,
            date: data?.date,
            ...(!data?.option_name && { isEdit: false }),
            ...(index === 0 && {
              subscribe_id: data?.id,
              is_send: data?.is_send,
              rowSpan: childrenRow.length,
              isEditDate: false,
            }),
          };
        });
      }) || []
    ).flat();
    setData(data);
  };

  const changeRow = (id, obj) =>
    setData((state) => {
      return state.map((item) => {
        if (item.id === id) {
          return { ...item, ...obj };
        }
        return item;
      });
    });

  const columns: ColumnConfig[] = [
    {
      columnProps: { flexGrow: 1.3, rowSpan: (rowData) => rowData?.rowSpan },
      header: '预约创建时间',
      cell: <DateTimeCell dataKey="create_at" format={BICESTER_FULL_DATE_HOURS_MINUTES} />,
    },
    {
      columnProps: { flexGrow: 0.7, rowSpan: (rowData) => rowData?.rowSpan },
      header: '客户名',
      cell: <UserNameCell dataKey="user_name" />,
    },
    {
      columnProps: { flexGrow: 0.9, rowSpan: (rowData) => rowData?.rowSpan },
      header: '手机号',
      cell: <Cell dataKey="phone" />,
    },
    {
      columnProps: { flexGrow: 0.8, rowSpan: (rowData) => rowData?.rowSpan },
      header: '会员等级',
      cell: <LevelCell dataKey="level" />,
    },
    {
      columnProps: { flexGrow: 1.2, rowSpan: (rowData) => rowData?.rowSpan },
      header: '预约日期',
      cell: (
        <SubscribeDateCell
          dataKey="date"
          displayFormat={BICESTER_FULL_DATE}
          editFormat={'yyyy/MM/dd'}
          setData={setData}
          onChangeRowData={changeRow}
          onChangeDate={updateSubscribeDate}
          onSend={subscribeSend}
          onReGet={onRefresh}
        />
      ),
    },
    {
      columnProps: { flexGrow: 1.2, colSpan: 3 },
      header: '预约项目',
      cell: <OptionCell dataKey="option_name" onChangeRowData={changeRow} />,
    },
    {
      columnProps: { flexGrow: 1 },
      header: '预约时间段',
      cell: <TimeSlotCell dataKey="number" onChangeRowData={changeRow} />,
    },
    {
      columnProps: { flexGrow: 1 },
      header: '预约详情',
      cell: <SubscribeDetailCell dataKey="content" onChangeRowData={changeRow} />,
    },
    {
      columnProps: { flexGrow: 1 },
      header: '预约状态',
      cell: <SubscribeStatusCell dataKey="status_name" />,
    },
    {
      columnProps: { flexGrow: 1 },
      header: '操作',
      cell: (
        <SubscribeOperationCell
          data={data}
          setData={setData}
          onChangeRowData={changeRow}
          onGetSurplusCount={getSurplusCount}
          onSave={updateOption}
          onUpdateStatus={updateStatus}
          onView={updateView}
          onReGet={onRefresh}
        />
      ),
    },
  ];

  // 10：00 - 22：00，每半小时为一个间隔
  const numberData = () => {
    const data = [];
    for (let i = 10; i <= 22; i++) {
      i === 22
        ? data.push({ label: `${i}:00`, value: `${i}:00` })
        : data.push({ label: `${i}:00`, value: `${i}:00` }, { label: `${i}:30`, value: `${i}:30` });
    }
    return data;
  };

  return (
    <div className="subscribe-service-manage">
      <Panel
        dividerHeight={10}
        leftShadow
        title="预约查询"
        loading={
          subscribeOptionLoading ||
          updateSubscribeDateLoading ||
          updateOptionLoading ||
          subscribeSendLoading ||
          updateStatusLoading ||
          updateViewLoading
        }
      >
        <FormikProvider value={formik}>
          <Form>
            <Card smDivider>
              <div className="inline-grid grid-cols-5" style={{ width: 'calc(100% - 40px)' }}>
                <Card.Input label="手机号" formKey="phone" />
                <Card.Input label="会员号" formKey="user_code" />
                <Card.Picker
                  label="预约项目"
                  formKey="option_id"
                  pickerData={subscribeOption.option_list}
                />
                <Card.CheckPicker
                  label="预约状态"
                  formKey="status"
                  pickerData={subscribeOption.status_list}
                />
                <Card.DateRangePicker label="预约日期" formKey="time_range" placement="bottomEnd" />
                <Card.Input label="姓名" formKey="user_name" />
                <Card.Picker
                  label="会员等级"
                  formKey="level"
                  pickerData={[
                    { label: '奕尊', value: 3 },
                    { label: '奕尚', value: 4 },
                  ]}
                />
                <Card.Picker
                  label="更新状态"
                  formKey="update_status"
                  pickerData={subscribeOption.update_status_list}
                />
                <Card.Picker
                  label="预约时间段"
                  formKey="number"
                  pickerData={numberData()}
                  placement="bottomEnd"
                />
              </div>
              <IconButton type="submit" className="inline-flex items-center w-[30px] ml-[10px]">
                <Image src="/resources/images/match.svg" />
              </IconButton>
            </Card>
          </Form>
        </FormikProvider>
      </Panel>
      <Panel
        title={`${Bicester[profile?.shopping_village]} | 预约列表`}
        subTitle={
          <>
            <span className="text-[12px]">总计 {total || 0} 条</span>
            <span className="ml-[20px] text-[12px]">贵宾休息室人数 {vip_counts_today || 0}</span>
            <span className="ml-[10px] text-[12px]">导购大使人数 {sa_counts_today || 0}</span>
          </>
        }
        leftShadow
        dividerHeight={10}
        style={{
          height:
            'max(calc(100vh - var(--headerHeight) - var(--navBarHeight) - var(--toolBarHeight) - 216px), auto)',
        }}
      >
        <Table
          style={{
            height: `max(${
              useContentHeight({
                withToolBar: true,
                withPanelHeader: true,
                withPanelPaddingTop: true,
                withPanelPaddingBottom: true,
              }) +
              (26 - 216)
            }px, ${
              // 20行 37 * 20 + 33 = 773
              773 +
              // LAYOUT_CONFIG.TABLE_MIN_ROWS_WITH_HEADER_HEIGHT +
              LAYOUT_CONFIG.TABLE_PAGINATION_WITH_MARGIN_HEIGHT
            }px})`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
          pageInfo={{
            page: current_page,
            total,
          }}
          onChange={onChange}
          data={data}
          loading={subscribeListLoading || getSurplusCountLoading}
          columns={columns}
        >
          <Table.Body
            height={Math.max(
              useContentHeight({
                withToolBar: true,
                withPanelHeader: true,
                withPanelPaddingTop: true,
                withPanelPaddingBottom: true,
                withTablePagination: true,
              }) +
                (26 - 216),
              // 20行 37 * 20 + 33 = 773
              773
              // LAYOUT_CONFIG.TABLE_MIN_ROWS_WITH_HEADER_HEIGHT
            )}
          />
          <Table.Pagination />
        </Table>
      </Panel>
    </div>
  );
};

export default Manage;
